import axios from 'axios'
import {
  fetchCombinedUserAssets,
  fetchProgramData,
  fetchUserAssets,
} from '@amplify/amp-api'

import EnvConstants, { isInternalEnv } from 'app/services/core/config.service'
import AuthApi from 'app/services/core/login.service'
import EntitlementService from 'app/services/core/entitlement.service'
import { fetchEnvironmentFlags } from 'app/services/api/amp-config-service'
import {
  ALL_GRADES,
  BETA_ASSET_KEY,
  FREE_ACCESS_PROGRAMS,
  GRADE_ASSETS,
  GRADE_PROGRAMS,
  HUB_ASSET_BUSINESS_KEY,
  HUB_ASSET_KEY,
  HUB_PROGRAMS,
  INTERACTIVE_CLASSROOM_NEW_FOR_BTS_22_ASSET_KEY,
} from 'app/constants/entitlement.constant'
import {
  collectAssetsFromEntitlements,
  collectAssetsFromUserPrograms,
} from 'app/utils/entitlement.utils'
import { Asset, UserProgram } from 'app/types/asset.types'
import { allowedSoundsAndSonidosGrades } from 'app/constants/home-content-buttons.constants'

// extracts program names from URL query parameters for testing purposes
// example: ?programs=CKLA,CAMINOS returns ['CKLA', 'CAMINOS']
const getSyntheticProgramsFromUri = () => {
  const params = new URLSearchParams(window.location.search)
  const hubProgramsFromUriStr = params.get('programs')
  return hubProgramsFromUriStr
    ? (hubProgramsFromUriStr.split(',') as HUB_PROGRAMS[])
    : []
}

export const addSyntheticHubAsset = (userAssets: Asset[]) => {
  userAssets.push({
    businessKey: HUB_ASSET_BUSINESS_KEY,
    name: 'Amplify Hub',
    metadata: {
      synthetic: true,
    },
  })
}

// generates synthetic assets for programs when users don't have real entitlements
// synthetic assets provide free access to educational content
export const addSyntheticAssets = (
  userAssets: Asset[],
  targetPrograms: HUB_PROGRAMS[]
) => {
  const syntheticAssets: Asset[] = []
  targetPrograms.forEach((targetProgram) => {
    if (GRADE_ASSETS[targetProgram as keyof typeof GRADE_ASSETS]) {
      ALL_GRADES.forEach((grade) => {
        // texas IMRA has grade restrictions for sounds content
        if (
          targetProgram !== HUB_PROGRAMS.TEXAS_IMRA ||
          allowedSoundsAndSonidosGrades.includes(grade)
        ) {
          const assetNamePattern = `${GRADE_ASSETS[targetProgram as keyof typeof GRADE_ASSETS][0]} ${grade}`
          const relevantAssets = userAssets.filter((userAsset: Asset) =>
            userAsset.name?.includes(assetNamePattern)
          )
          // only add synthetic asset if user doesn't already have the real asset for this grade level
          const userHasNoAssetForGrade = relevantAssets.length === 0
          if (userHasNoAssetForGrade) {
            const syntheticAsset = {
              businessKey:
                `synthetic-asset-${targetProgram}-${grade}`.toLowerCase(),
              name: assetNamePattern,
              metadata: { grade },
            }
            syntheticAssets.push(syntheticAsset)
            userAssets.push(syntheticAsset)
          }
        }
      })
    }
  })

  // always ensure user has access to the hub
  addSyntheticHubAsset(userAssets)

  if (syntheticAssets.length > 0) {
    /* eslint-disable-next-line no-console */
    console.log('added synthetic assets', syntheticAssets)
  }
  return userAssets
}

// adds synthetic programs for testing in internal environments
// filters out existing programs to avoid duplicates
const addSyntheticPrograms = (
  userPrograms: UserProgram[],
  targetPrograms: HUB_PROGRAMS[]
) => {
  if (targetPrograms.length > 0) {
    Object.keys(GRADE_PROGRAMS).forEach((hubProgram) => {
      GRADE_PROGRAMS[hubProgram as keyof typeof GRADE_PROGRAMS].forEach(
        (programNamePattern) => {
          userPrograms = userPrograms.filter(
            (userProgram) => !userProgram.name?.includes(programNamePattern)
          )
        }
      )
    })

    targetPrograms.forEach((targetProgram) => {
      if (GRADE_PROGRAMS[targetProgram as keyof typeof GRADE_PROGRAMS]) {
        ALL_GRADES.forEach((grade) => {
          if (
            targetProgram !== HUB_PROGRAMS.TEXAS_IMRA ||
            allowedSoundsAndSonidosGrades.includes(grade)
          ) {
            userPrograms.push({
              businessKey:
                `synthetic-program-${targetProgram}-${grade}`.toLowerCase(),
              name: `${GRADE_PROGRAMS[targetProgram as keyof typeof GRADE_PROGRAMS][0]} ${grade}`,
              contents: [],
            })
          }
        })
      }
    })
  }

  /* eslint-disable-next-line no-console */
  console.log(
    'added synthetic programs',
    userPrograms.filter((userProgram) =>
      userProgram.businessKey?.includes('synthetic-program')
    )
  )
  return userPrograms
}

// fetches user's licensed assets and adds synthetic assets based on environment
export const getUserAssets = async (): Promise<Asset[]> => {
  const userBk = AuthApi.getUserBk()
  const isStudent = Boolean(
    EntitlementService.isStudentInRoles(AuthApi.getRoles())
  )
  const flags = await fetchEnvironmentFlags()
  const shouldCheckOpenEntitlement =
    Boolean(flags.isOpenEntitlementEnabled) && !isStudent

  const data: Asset[] = shouldCheckOpenEntitlement
    ? await fetchCombinedUserAssets(userBk)
    : await fetchUserAssets(null, null, userBk)

  const isUserEntitledToHub = EntitlementService.checkIfAssetIsPresentByBk(
    data,
    HUB_ASSET_BUSINESS_KEY
  )
  const uriPrograms = getSyntheticProgramsFromUri()

  // if not prod AND there are programs listed in the url,
  // add synthetic assets for those
  if (isInternalEnv() && uriPrograms.length > 0) {
    // always ensure user has access to the hub
    if (!isUserEntitledToHub) {
      addSyntheticHubAsset(data)
    }
    return addSyntheticAssets(data, uriPrograms)
  }

  // if user is not entitled to hub,
  // add synthetic assets for free programs (currently CKLA3)
  if (!isUserEntitledToHub) {
    addSyntheticHubAsset(data)
    return addSyntheticAssets(data, FREE_ACCESS_PROGRAMS)
  }

  return data
}

export const getClassAssets = async () => {
  const token = AuthApi.getAccessToken()
  const url = `${EnvConstants.RETAIL_URL}/api/licensing/my/class/assets`
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }

  return axios.get(url, config).then(({ data }) => data)
}

export const getUserPrograms = async (): Promise<UserProgram[]> => {
  const userBk = AuthApi.getUserBk()
  const data: UserProgram[] = await fetchProgramData(userBk)

  const uriPrograms = getSyntheticProgramsFromUri()

  // if not prod AND there are programs listed in the url,
  // add synthetic programs for those
  if (isInternalEnv() && uriPrograms.length > 0) {
    return addSyntheticPrograms(data, uriPrograms)
  }

  return data
}

// checks if all specified assets exist within the same content level
const hasAssetsOnContentLevel = (contents: Asset[][], assetsBks: string[]) =>
  contents.some((contentAssets) =>
    assetsBks.every((asset) =>
      EntitlementService.checkIfAssetIsPresentByBk(contentAssets, asset)
    )
  )

export const hasDEForCKLA = async () => {
  const { entitlement } = await getClassAssets()
  if (!entitlement) return false
  return hasAssetsOnContentLevel(collectAssetsFromEntitlements(entitlement), [
    BETA_ASSET_KEY,
    HUB_ASSET_BUSINESS_KEY,
  ])
}

// checks if a user has "Interactive Classroom (NEW FOR BTS 22)" and "CKLA" assets on the same user content level
export const hasICAndCKLAOnUserContentLevel = async () => {
  const programs = await getUserPrograms()

  return hasAssetsOnContentLevel(collectAssetsFromUserPrograms(programs), [
    INTERACTIVE_CLASSROOM_NEW_FOR_BTS_22_ASSET_KEY,
    HUB_ASSET_KEY,
  ])
}
